import { useEffect, useState, useCallback } from "react";
import { Printer, Pencil } from "lucide-react";
import api from "@/lib/api";
import OpReceipt from "@/components/op/OpReceipt";
import OpHeader from "@/components/op/OpHeader";
import CorrectionDialog from "@/components/op/CorrectionDialog";

const MOD = { PENGAMBILAN: "Pengambilan", SETORAN: "Setoran", RIJEK_SUSULAN: "Rijek Susulan", RIJEK_KIRIM: "Kirim Perbaikan", RIJEK_TERIMA: "Terima Perbaikan" };
const MARKER = {
  REVISI: { t: "REVISI", c: "bg-blue-100 text-blue-700" },
  DIKOREKSI: { t: "SUDAH DIKOREKSI", c: "bg-amber-100 text-amber-700" },
  DIBATALKAN: { t: "DIBATALKAN", c: "bg-rose-100 text-rose-700" },
};
const REQ_STATUS = {
  MENUNGGU: "bg-amber-100 text-amber-700",
  DISETUJUI: "bg-emerald-100 text-emerald-700",
  DITOLAK: "bg-rose-100 text-rose-700",
  PERLU_DITINJAU_ULANG: "bg-orange-100 text-orange-700",
};

const fmtID = (s) => { if (!s) return "-"; const p = String(s).slice(0, 10).split("-"); return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : s; };

export default function OpHistory() {
  const [rows, setRows] = useState([]);
  const [reqMap, setReqMap] = useState({});
  const [receipt, setReceipt] = useState(null);
  const [correct, setCorrect] = useState(null);

  const load = useCallback(() => {
    api.get("/op/history?limit=200").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/op/corrections").then((r) => {
      const m = {};
      for (const q of r.data) { if (!m[q.op_no] || (q.created_at || "") > (m[q.op_no].created_at || "")) m[q.op_no] = q; }
      setReqMap(m);
    }).catch(() => setReqMap({}));
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = (op_no) => api.get(`/op/receipt/${op_no}`).then((r) => setReceipt(r.data)).catch(() => {});

  return (
    <div className="w-full pb-10" data-testid="op-history">
      <OpHeader title="RIWAYAT MODUL" />

      {rows.length === 0 ? <div className="text-slate-400 text-sm">Belum ada transaksi.</div> : (
        <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
          {rows.map((h) => {
            const mk = MARKER[h.revision_marker];
            const req = reqMap[h.op_no];
            const pending = req && (req.status === "MENUNGGU" || req.status === "PERLU_DITINJAU_ULANG");
            const hasReceipt = !["RIJEK_KIRIM", "RIJEK_TERIMA"].includes(h.source_module);
            const canCorrect = ["PENGAMBILAN", "SETORAN", "RIJEK_SUSULAN"].includes(h.source_module) && h.status === "POSTED" && !pending;
            return (
              <div key={h.op_no} className="flex items-center justify-between gap-2 px-4 py-3" data-testid={`op-history-row-${h.op_no}`}>
                <button type="button" onClick={() => hasReceipt && open(h.op_no)} className={`min-w-0 text-left ${hasReceipt ? "cursor-pointer" : "cursor-default"}`} data-testid={`op-history-open-${h.op_no}`}>
                  <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-blue-700 underline decoration-dotted underline-offset-2">{h.op_no}</span> · {MOD[h.source_module] || h.source_module}
                    {mk && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${mk.c}`} data-testid={`op-history-marker-${h.op_no}`}>{mk.t}</span>}
                    {req && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${REQ_STATUS[req.status] || "bg-slate-100 text-slate-600"}`} data-testid={`op-history-req-${h.op_no}`}>{req.action === "PEMBATALAN" ? "Batal" : "Koreksi"}: {req.status.replace(/_/g, " ")}</span>}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{h.worker_name || "-"} · {fmtID(h.date)} · oleh {h.entry_by}</div>
                </button>
                <div className="flex items-center gap-3 shrink-0">
                  {canCorrect && (
                    <button onClick={() => setCorrect({ opNo: h.op_no, module: h.source_module })} className="inline-flex items-center gap-1 text-sm text-amber-700" data-testid={`op-history-correct-${h.op_no}`}><Pencil className="w-4 h-4" />Koreksi</button>
                  )}
                  {hasReceipt && (
                    <button onClick={() => open(h.op_no)} className="inline-flex items-center gap-1 text-sm text-blue-700" data-testid={`op-history-print-${h.op_no}`}><Printer className="w-4 h-4" />Struk</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {receipt && <OpReceipt doc={receipt} onClose={() => setReceipt(null)} />}
      {correct && <CorrectionDialog opNo={correct.opNo} module={correct.module} onClose={() => setCorrect(null)} onDone={load} />}
    </div>
  );
}
